// src/TodoPage.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchTasks,
  createTask,
  updateTask,
  deleteTask,
} from "./api";
import { useAuth } from "./AuthContext";

const TodoPage = ({ leads }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState("");
  const [linkedLeadId, setLinkedLeadId] = useState("");
  const [dueDateTime, setDueDateTime] = useState("");
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    loadTasks();
  }, [token]);

  const loadTasks = async () => {
    try {
      const data = await fetchTasks(token);
      setTasks(data);
    } catch (err) {
      console.error("Errore caricamento task:", err);
    }
  };

  const handleAddTask = async () => {
    if (!newTask.trim()) return;
    const task = {
      text: newTask.trim(),
      completed: false,
      due_datetime: dueDateTime || null,
      lead_id: linkedLeadId || null,
    };

    try {
      const saved = await createTask(task, token);
      setTasks([...tasks, saved]);
      setNewTask("");
      setLinkedLeadId("");
      setDueDateTime("");
    } catch (err) {
      console.error("Errore creazione task:", err);
    }
  };

  const toggleTask = async (task) => {
    // Aggiorna immediatamente lo stato locale per feedback immediato
    const optimisticUpdate = tasks.map(t => 
      t.id === task.id ? { ...t, completed: !t.completed } : t
    );
    setTasks(optimisticUpdate);

    try {
      const updated = await updateTask(task.id, { ...task, completed: !task.completed }, token);
      // Conferma l'aggiornamento con i dati dal server
      setTasks(prevTasks => prevTasks.map(t => t.id === task.id ? updated : t));
    } catch (err) {
      console.error("Errore aggiornamento task:", err);
      // Rollback in caso di errore
      setTasks(tasks);
    }
  };

  const handleArchiveTask = async (task) => {
    // Aggiorna immediatamente lo stato locale
    const optimisticUpdate = tasks.map(t => 
      t.id === task.id ? { ...t, archived: true } : t
    );
    setTasks(optimisticUpdate);

    try {
      const updated = await updateTask(task.id, { ...task, archived: true }, token);
      // Conferma l'aggiornamento con i dati dal server
      setTasks(prevTasks => prevTasks.map(t => t.id === task.id ? updated : t));
    } catch (err) {
      console.error("Errore archiviazione task:", err);
      // Rollback in caso di errore
      setTasks(tasks);
    }
  };

  const handleRestoreTask = async (task) => {
    // Aggiorna immediatamente lo stato locale
    const optimisticUpdate = tasks.map(t => 
      t.id === task.id ? { ...t, archived: false } : t
    );
    setTasks(optimisticUpdate);

    try {
      const updated = await updateTask(task.id, { ...task, archived: false }, token);
      // Conferma l'aggiornamento con i dati dal server
      setTasks(prevTasks => prevTasks.map(t => t.id === task.id ? updated : t));
    } catch (err) {
      console.error("Errore ripristino task:", err);
      // Rollback in caso di errore
      setTasks(tasks);
    }
  };

  const handleLeadClick = (leadId) => {
    if (leadId) {
      navigate(`/lead/${leadId}`);
    }
  };

  // Funzioni per la gestione del calendario
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return firstDay === 0 ? 6 : firstDay - 1; // Lunedì = 0
  };

  const getTasksForDate = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return tasks.filter(task => {
      if (!task.due_datetime) return false;
      const taskDate = new Date(task.due_datetime).toISOString().split('T')[0];
      return taskDate === dateStr;
    });
  };

  const getTasksWithoutDate = () => {
    return tasks.filter(task => !task.due_datetime);
  };

  const changeMonth = (increment) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + increment, 1));
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Giorni vuoti all'inizio del mese
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="h-24 border border-gray-200"></div>
      );
    }

    // Giorni del mese
    for (let day = 1; day <= daysInMonth; day++) {
      const dayTasks = getTasksForDate(day);
      const today = new Date();
      const isToday = today.getDate() === day && 
                     today.getMonth() === currentDate.getMonth() && 
                     today.getFullYear() === currentDate.getFullYear();

      days.push(
        <div key={day} className={`h-24 border border-gray-200 p-1 ${isToday ? 'bg-blue-50' : 'bg-white'} overflow-y-auto`}>
          <div className={`text-sm font-semibold mb-1 ${isToday ? 'text-blue-600' : 'text-gray-600'}`}>
            {day}
          </div>
          {dayTasks.map(task => (
            <div
              key={task.id}
              className={`text-xs p-1 mb-1 rounded border-l-2 ${
                task.archived 
                  ? 'bg-gray-300 text-gray-600 border-gray-500 opacity-60' 
                  : task.completed 
                    ? 'bg-green-100 text-green-800 border-green-400 line-through' 
                    : 'bg-blue-100 text-blue-800 border-blue-400'
              }`}
            >
              <div className="font-medium truncate" title={task.text}>
                {task.text}
              </div>
              {task.lead_id && (
                <div 
                  className={`text-xs cursor-pointer hover:underline ${
                    task.archived ? 'text-gray-500' : 'text-purple-600'
                  }`}
                  onClick={() => !task.archived && handleLeadClick(task.lead_id)}
                  title={task.archived ? "Task archiviato" : "Clicca per andare al lead"}
                >
                  🔗 {leads.find(l => l.id === task.lead_id)?.nome || "N/A"}
                </div>
              )}
              <div className="flex gap-1 mt-1">
                {!task.archived ? (
                  <>
                    <button
                      onClick={() => toggleTask(task)}
                      className={`text-xs px-1 rounded text-white hover:opacity-80 ${
                        task.completed ? 'bg-orange-400' : 'bg-green-400'
                      }`}
                      title={task.completed ? "Riapri" : "Completa"}
                    >
                      {task.completed ? "↩️" : "✅"}
                    </button>
                    <button
                      onClick={() => handleArchiveTask(task)}
                      className="text-xs px-1 rounded bg-gray-400 text-white hover:bg-gray-500"
                      title="Archivia"
                    >
                      📦
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleRestoreTask(task)}
                    className="text-xs px-1 rounded bg-blue-400 text-white hover:bg-blue-500"
                    title="Ripristina"
                  >
                    🔄
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }

    return days;
  };

  const monthNames = [
    "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
    "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"
  ];

  const dayNames = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-6">📝 To Do Calendar</h1>

      {/* Form per aggiungere nuovi task */}
      <div className="bg-white p-4 rounded shadow mb-6">
        <h2 className="text-lg font-semibold mb-3">Aggiungi Nuovo Task</h2>
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <input
            type="text"
            placeholder="Cosa devi fare?"
            className="border p-2 rounded w-full md:w-1/3"
            value={newTask}
            onChange={(e) => setNewTask(e.target.value)}
          />
          <select
            className="border p-2 rounded w-full md:w-1/4"
            value={linkedLeadId}
            onChange={(e) => setLinkedLeadId(e.target.value)}
          >
            <option value="">Nessun Lead Collegato</option>
            {leads.map((lead) => (
              <option key={lead.id} value={lead.id}>
                {lead.nome}
              </option>
            ))}
          </select>
          <input
            type="datetime-local"
            className="border p-2 rounded w-full md:w-1/4"
            value={dueDateTime}
            onChange={(e) => setDueDateTime(e.target.value)}
          />
          <button
            onClick={handleAddTask}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            ➕ Aggiungi
          </button>
        </div>
      </div>

      {/* Controlli del calendario */}
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={() => changeMonth(-1)}
          className="bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600"
        >
          ← Mese Precedente
        </button>
        <h2 className="text-xl font-semibold">
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h2>
        <button
          onClick={() => changeMonth(1)}
          className="bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600"
        >
          Mese Successivo →
        </button>
      </div>

      {/* Calendario */}
      <div className="bg-white rounded shadow overflow-hidden">
        {/* Header giorni della settimana */}
        <div className="grid grid-cols-7 bg-gray-100">
          {dayNames.map(day => (
            <div key={day} className="p-2 text-center font-semibold text-gray-600 border-r border-gray-200">
              {day}
            </div>
          ))}
        </div>
        
        {/* Griglia del calendario */}
        <div className="grid grid-cols-7">
          {renderCalendarDays()}
        </div>
      </div>

      {/* Task senza data */}
      {getTasksWithoutDate().length > 0 && (
        <div className="mt-6 bg-white p-4 rounded shadow">
          <h3 className="text-lg font-semibold mb-3">Task Senza Data di Scadenza</h3>
          <div className="space-y-2">
            {getTasksWithoutDate().map(task => (
              <div
                key={task.id}
                className={`p-3 rounded border ${
                  task.archived 
                    ? 'bg-gray-200 text-gray-600 border-gray-400 opacity-60' 
                    : task.completed 
                      ? 'bg-green-50 text-green-700 border-green-200 line-through' 
                      : 'bg-blue-50 border-blue-200'
                }`}
              >
                <div className={`font-medium ${task.completed ? 'line-through' : ''}`}>
                  {task.text}
                </div>
                {task.lead_id && (
                  <div 
                    className={`text-sm cursor-pointer hover:underline mt-1 ${
                      task.archived ? 'text-gray-500' : 'text-purple-600'
                    }`}
                    onClick={() => !task.archived && handleLeadClick(task.lead_id)}
                    title={task.archived ? "Task archiviato" : "Clicca per andare al lead"}
                  >
                    🔗 Lead: {leads.find(l => l.id === task.lead_id)?.nome || "N/A"}
                  </div>
                )}
                <div className="flex gap-2 mt-2">
                  {!task.archived ? (
                    <>
                      <button
                        onClick={() => toggleTask(task)}
                        className={`px-2 py-1 rounded text-white hover:opacity-80 text-sm ${
                          task.completed ? 'bg-orange-400' : 'bg-green-400'
                        }`}
                        title={task.completed ? "Riapri" : "Completa"}
                      >
                        {task.completed ? "↩️ Riapri" : "✅ Completa"}
                      </button>
                      <button
                        onClick={() => handleArchiveTask(task)}
                        className="px-2 py-1 rounded bg-gray-400 text-white hover:bg-gray-500 text-sm"
                        title="Archivia"
                      >
                        📦 Archivia
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleRestoreTask(task)}
                      className="px-2 py-1 rounded bg-blue-400 text-white hover:bg-blue-500 text-sm"
                      title="Ripristina"
                    >
                      🔄 Ripristina
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TodoPage;